import { useEffect, useRef } from 'react';
import { RouterProvider } from 'react-router-dom';
import { router } from './router';
import { useAuthStore } from './stores/authStore';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from './lib/firebase';
import { trackPageView } from './lib/analytics';
import { captureAcquisitionAttribution } from './lib/acquisition';
import { Toast } from './components/ui/Toast';
import { useToastStore } from './stores/toastStore';
import { parseImpersonationClaims } from './lib/impersonation';
import {
  parseForegroundNotification,
  removeFcmRegistration,
  resolvePushEnabled,
  subscribeToForegroundMessages,
  syncFcmRegistration,
} from './lib/messaging';

const SETTINGS_DOC_ID = 'preferences';

function App() {
  const {
    user,
    setUser,
    setUserData,
    setImpersonation,
    setLoading,
    fetchUserData,
    impersonation,
  } = useAuthStore();
  const addToast = useToastStore((state) => state.addToast);
  const prevUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const trackCurrentPage = () => {
      const { pathname, search } = router.state.location;
      captureAcquisitionAttribution();
      trackPageView(`${pathname}${search}`);
    };

    trackCurrentPage();
    return router.subscribe(trackCurrentPage);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const idToken = await firebaseUser.getIdTokenResult();
        setImpersonation(parseImpersonationClaims(firebaseUser.uid, idToken.claims));

        // Firebase userをアプリ用のuser形式に変換
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          emailVerified: firebaseUser.emailVerified,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
        });
        
        // Firestoreからユーザーデータ（role等）を取得
        await fetchUserData(firebaseUser.uid);
      } else {
        setUser(null);
        setUserData(null);
        setImpersonation(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [setUser, setUserData, setImpersonation, setLoading, fetchUserData]);

  useEffect(() => {
    if (!user?.uid || impersonation) {
      return;
    }

    const settingsRef = doc(db, 'users', user.uid, 'settings', SETTINGS_DOC_ID);
    const unsubscribe = onSnapshot(
      settingsRef,
      (snapshot) => {
        const pushEnabled = resolvePushEnabled(snapshot.data()?.notifications);
        void syncFcmRegistration({
          userId: user.uid,
          pushEnabled,
          allowPermissionPrompt: false,
        });
      },
      (error) => {
        console.error('[FCM] Failed to subscribe notification settings:', error);
      }
    );

    return () => unsubscribe();
  }, [user?.uid, impersonation]);

  useEffect(() => {
    if (!user?.uid || impersonation) {
      return;
    }

    let isDisposed = false;
    let unsubscribeForeground: () => void = () => {};

    void subscribeToForegroundMessages((payload) => {
      const notification = parseForegroundNotification(payload);
      if (!notification) {
        return;
      }

      const message = notification.body
        ? `${notification.title}: ${notification.body}`
        : notification.title;
      addToast('info', message, 5000);
    })
      .then((unsubscribe) => {
        if (isDisposed) {
          unsubscribe();
          return;
        }
        unsubscribeForeground = unsubscribe;
      })
      .catch((error) => {
        console.error('[FCM] Failed to subscribe foreground messages:', error);
      });

    return () => {
      isDisposed = true;
      unsubscribeForeground();
    };
  }, [user?.uid, impersonation, addToast]);

  useEffect(() => {
    const currentUserId = user?.uid ?? null;
    const previousUserId = prevUserIdRef.current;

    if (previousUserId && previousUserId !== currentUserId) {
      void removeFcmRegistration(previousUserId);
    }

    prevUserIdRef.current = currentUserId;
  }, [user?.uid]);

  return (
    <>
      <RouterProvider router={router} />
      <Toast />
    </>
  );
}

export default App;
